/**
 * ClubList.tsx
 *
 * Presentational list of the user's clubs (#1976 Phase 2). Extracted from the
 * retired ClubSwitcherModal so club switching can render inside the shared
 * BottomSheet — both standalone (TopBar club switcher) and as the conditional
 * "Clubs" section of the chat context sheet (shown only when the user belongs
 * to >1 club, per ADR-UX-003).
 *
 * Purely presentational: the active club + selection/navigation are owned by
 * the caller. Pure selection surface — club creation lives in Settings ›
 * Clubs (#2339), not in the switcher.
 */

import { AlertCircle, Check } from 'lucide-react';
import type { ClubWithRole } from '../lib/api/clubs';

interface ClubListProps {
  clubs: ClubWithRole[];
  activeClubSlug?: string | null;
  onSelectClub: (clubSlug: string) => void;
  isLoading?: boolean;
  error?: Error | null;
}

export default function ClubList({
  clubs,
  activeClubSlug,
  onSelectClub,
  isLoading = false,
  error = null,
}: ClubListProps) {
  return (
    <div>
      {error ? (
        // Real error state — never leave the sheet stuck on "Loading clubs..."
        // when the query settles without data (e.g. an error response).
        <div className="p-4">
          <div className="flex items-start gap-3 p-4 bg-oro-error-soft border border-oro-error-border rounded-lg">
            <AlertCircle className="w-5 h-5 text-oro-error flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-text-primary">Unable to load clubs</div>
              <div className="text-sm text-text-secondary mt-1">{error.message || 'Please try again later.'}</div>
            </div>
          </div>
        </div>
      ) : isLoading ? (
        <div className="p-8 text-center text-text-secondary">Loading clubs...</div>
      ) : clubs.length > 0 ? (
        <nav className="py-2">
          {clubs.map((club) => {
            const isCurrentClub = club.slug === activeClubSlug;
            return (
              <button
                key={club.id}
                onClick={() => onSelectClub(club.slug)}
                className="flex items-center gap-3 px-4 py-3 min-h-11 w-full text-left hover:bg-oro-surface-alt transition-colors"
                aria-current={isCurrentClub ? 'true' : undefined}
              >
                {club.hero_image_url ? (
                  <img
                    src={club.hero_image_url}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    loading="lazy"
                    width={40}
                    height={40}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-oro-surface-alt flex-shrink-0" />
                )}

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary truncate">{club.name}</div>
                  {club.city && club.state && (
                    <div className="text-sm text-text-secondary truncate">
                      {club.city}, {club.state}
                    </div>
                  )}
                </div>

                {isCurrentClub && <Check className="w-5 h-5 text-oro-gold flex-shrink-0" />}
              </button>
            );
          })}
        </nav>
      ) : (
        <div className="p-8 text-center text-text-secondary">No clubs yet</div>
      )}
    </div>
  );
}
